import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import cn from "classnames";
import type { FilterCondition } from "epsm-api-types";
import {
	SCREENER_COLUMNS,
	type ScreenerColumn,
	type ScreenerFilterData,
} from "epsm-api-types/models";
import { type ChangeEvent, useEffect, useMemo, useState } from "react";
import type { SingleValue } from "react-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatValue } from "@/components/widgets/helpers/FormattingHelpers";
import {
	type FilterDataContextProps,
	useFilterData,
} from "@/context/FilterDataContext";
import {
	useWidgetView,
	type ViewState,
	type WidgetViewContextProps,
} from "@/context/WidgetViewContext";
import type { Option } from "@/interfaces/Option";
import type { ValueRange } from "@/interfaces/ValueRange";
import { flattenSubfieldsToDropdownOptions } from "@/lib/utils/flattenSubfieldsToDropdownOptions";
import { findClosestValue } from "../../lib/helpers/findClosestValue";
import { getCurrencySymbol } from "../../lib/helpers/getCurrencySymbol";
import { getValueFromPercents } from "../../lib/helpers/getValueFromPercents";
import { getValueToPercents } from "../../lib/helpers/getValueToPercents";
import { isEmptyInputValue } from "../../lib/helpers/isEmptyInputValue";
import { Dropdown } from "./ui/Dropdown";
import { RangePicker } from "./ui/RangePicker";

const SCREEN_WIDTHS = {
	SMALL: 831,
	MEDIUM: 1200,
	LARGE: 1600,
};

export const numberInputRegExp: RegExp = /^-?(0(\.\d*)?|[1-9]\d*(\.\d*)?)$/;

export const NumberFilters = ({
	width,
	refetch,
	hasNull,
}: {
	width?: number;
	refetch: UseInfiniteQueryResult["refetch"];
	hasNull: (data: ScreenerFilterData | null) => boolean | null;
}) => {
	const { selectedColumn } = useWidgetView() as {
		selectedColumn: ViewState<ScreenerColumn>;
	} & WidgetViewContextProps;
	const {
		updateResults,
		value,
		setValue,
		filterData,
		selectedSubField,
		setSelectedSubField,
		getSubFieldName,
		hasRangePickerValueChangedRef,
	} = useFilterData() as {
		filterData: ScreenerFilterData;
	} & FilterDataContextProps;

	const columnOptions = selectedColumn?.Screener
		?.options as Intl.NumberFormatOptions;

	const {
		notation: _notation,
		style: _style,
		currency: _currency,
		...optionsWithoutNotationAndStyleAndCurrency
	} = columnOptions
		? { ...columnOptions, maximumFractionDigits: 5 }
		: { maximumFractionDigits: 5 };

	const isPercentStyle = columnOptions?.style === "percent";
	const isCurrencyStyle = columnOptions?.style === "currency";

	const [inputValue, setInputValue] = useState<ValueRange>(() => {
		if (isPercentStyle && value) {
			return {
				min: getValueToPercents(value?.min, isPercentStyle),
				max: getValueToPercents(value?.max, isPercentStyle),
			};
		}
		return value;
	});

	const [isInputFocused, setIsInputFocused] = useState<{
		min: boolean;
		max: boolean;
	}>({ min: false, max: false });

	const closestMin = useMemo(() => {
		return findClosestValue(
			getValueFromPercents(inputValue?.min, isPercentStyle),
			filterData?.items as number[],
		);
	}, [inputValue?.min, filterData?.items, isPercentStyle]);

	const closestMax = useMemo(() => {
		return findClosestValue(
			getValueFromPercents(inputValue?.max, isPercentStyle),
			filterData?.items as number[],
		);
	}, [inputValue?.max, filterData?.items, isPercentStyle]);

	useEffect(() => {
		if (!hasRangePickerValueChangedRef?.current) return;

		const minValue = getValueToPercents(value.min, isPercentStyle);
		const maxValue = getValueToPercents(value.max, isPercentStyle);

		if (String(closestMin) !== minValue)
			setInputValue((prev) => ({ ...prev, min: minValue }));

		if (String(closestMax) !== maxValue)
			setInputValue((prev) => ({ ...prev, max: maxValue }));

		hasRangePickerValueChangedRef.current = false;
	}, [
		value?.min,
		value?.max,
		closestMin,
		closestMax,
		hasRangePickerValueChangedRef,
		isPercentStyle,
	]);

	const subFields = flattenSubfieldsToDropdownOptions(
		selectedColumn?.Screener?.subFields || [],
	);

	const column = SCREENER_COLUMNS.find(
		(column) => column.name === selectedColumn?.Screener?.name,
	);

	const columnAbbr =
		column?.titleAbbreviation ?? column?.titleShort ?? column?.title;
	const columnTitle =
		column?.title === columnAbbr
			? column?.title
			: `${column?.title} (${columnAbbr})`;

	const isValidNumberInput = (value: string): boolean => {
		if (value === "" || value === "-") return true;

		if (numberInputRegExp.test(value)) return true;

		return false;
	};

	const getRawInputValue = (inputValue: string): string => inputValue;

	const getFormattedInputValue = (inputValue: string): string =>
		formatValue(
			inputValue,
			selectedColumn.Screener.type,
			optionsWithoutNotationAndStyleAndCurrency,
		);

	const getDisplayValue = (inputValue: string, isFocused: boolean): string => {
		if (isEmptyInputValue(inputValue)) {
			return inputValue === "-" ? "-" : "";
		}

		if (isFocused) {
			return getRawInputValue(inputValue);
		}

		return getFormattedInputValue(inputValue);
	};

	const handleNumberInputChange = (
		event: ChangeEvent<HTMLInputElement>,
		itemKey: keyof ValueRange,
	) => {
		const rawValue = event.target.value.trim();

		if (!isValidNumberInput(rawValue)) return;

		setInputValue((prev) => ({
			...prev,
			[itemKey]: rawValue,
		}));
	};

	const handleNumberInputFocus = (itemKey: keyof ValueRange) => {
		setIsInputFocused((prev) => ({
			...prev,
			[itemKey]: true,
		}));
	};

	const handleNumberInputBlur = (itemKey: keyof ValueRange) => {
		setIsInputFocused((prev) => ({
			...prev,
			[itemKey]: false,
		}));

		const currentValue = inputValue[itemKey];

		const normalizedValue = getValueFromPercents(currentValue, isPercentStyle);

		if (normalizedValue !== "") {
			setInputValue((prev) => ({
				...prev,
				[itemKey]: String(Number(currentValue)),
			}));

			const closestValue = findClosestValue(
				normalizedValue,
				filterData.items as number[],
			);

			setValue((prev) => ({
				...prev,
				[itemKey]: closestValue,
			}));
		} else {
			setValue((prev) => ({
				...prev,
				[itemKey]: "",
			}));
		}
	};

	const handleUpdateResults = () => {
		const newResult: Array<{
			name: string;
			condition: FilterCondition;
			value: unknown;
		}> = [];

		const minValue = getValueFromPercents(inputValue.min, isPercentStyle);

		if (minValue !== "") {
			newResult.push({
				name: getSubFieldName() ?? selectedColumn.Screener.name,
				condition: "gte",
				value: minValue,
			});
		}
		const maxValue = getValueFromPercents(inputValue.max, isPercentStyle);

		if (maxValue !== "") {
			newResult.push({
				name: getSubFieldName() ?? selectedColumn.Screener.name,
				condition: "lte",
				value: maxValue,
			});
		}

		updateResults(newResult, refetch);
	};

	return (
		<div className="flex flex-col w-full" data-testid="number-filters">
			<div
				className={cn("flex items-center", {
					"flex-col": width && width < SCREEN_WIDTHS.LARGE,
				})}
			>
				<div className="flex flex-col items-stretch w-full h-full">
					<div className="flex flex-col gap-4">
						<h3 className="text-[var(--light-gray)]">{columnTitle}</h3>
						{selectedColumn?.Screener?.subFields?.length && (
							<Dropdown
								options={subFields}
								maxMenuHeight={80}
								placeholder={subFields[0]?.label}
								value={selectedSubField || subFields[0]}
								onChange={(newValue) =>
									setSelectedSubField(newValue as SingleValue<Option>)
								}
								isTypeList={false}
								className="w-[120px] self-end mb-2"
							/>
						)}
					</div>

					<div className={cn("flex justify-center gap-1 mb-3 mt-5 w-full")}>
						{["Min", "Max"].map((item) => {
							const itemKey = item.toLowerCase() as keyof ValueRange;
							const isFocused = isInputFocused[itemKey];

							return (
								<div key={item} className="w-full">
									<div className="flex items-start mb-2">
										<p className="text-white/80 mr-3">{item}</p>
									</div>

									<div
										className={cn(
											"relative w-full border border-gray hover:border-light-gray/50 focus:border-light-gray/70 rounded-md max-h-[18px] min-h-[18px] h-full p-0 outline-none",
										)}
									>
										{isPercentStyle && (
											<span
												className="absolute right-2 h-[15px]"
												data-testid="percent-style-symbol"
											>
												%
											</span>
										)}
										{isCurrencyStyle && (
											<span
												className="absolute left-2 h-[15px]"
												data-testid="currency-style-symbol"
											>
												{getCurrencySymbol(columnOptions)}
											</span>
										)}
										<Input
											className={cn(
												"text-right max-h-full min-h-full border-none outline-none px-2",
												{
													"pr-4.5": isPercentStyle,
													"pl-4.5": isCurrencyStyle,
												},
											)}
											data-testid={item}
											value={getDisplayValue(inputValue?.[itemKey], isFocused)}
											onChange={(event) =>
												handleNumberInputChange(event, itemKey)
											}
											onFocus={() => handleNumberInputFocus(itemKey)}
											onBlur={() => handleNumberInputBlur(itemKey)}
										/>
									</div>
								</div>
							);
						})}
					</div>
					{!!filterData?.items?.length && !hasNull(filterData) && (
						<RangePicker />
					)}
				</div>
			</div>

			<Button
				className={cn("self-end h-[18px]", {
					"self-start": width && width < SCREEN_WIDTHS.SMALL,
				})}
				variant="outline"
				disabled={
					(isEmptyInputValue(inputValue?.min) &&
						isEmptyInputValue(inputValue?.max)) ||
					(isEmptyInputValue(value?.min) && isEmptyInputValue(value?.max))
				}
				onClick={handleUpdateResults}
				onMouseDown={(e) => e.preventDefault()}
			>
				Add
			</Button>
		</div>
	);
};
